import type { ChangeType } from '@platform/shared-types';

/**
 * THE BLAST-RADIUS CORRECTNESS GUARD.
 *
 * The rule, in one sentence: a blast-radius number may only be displayed, or
 * published against, when it answers the EXACT question currently on screen —
 * the same change type AND the same effective instant. A number computed for a
 * different question, or one that is still in flight, or one whose fetch
 * failed, is not an answer. The UI must then render an explicit "—" and refuse
 * to publish a material change.
 *
 * `0` IS AN ANSWER. "0 acceptances go stale" and "we do not know how many go
 * stale" are different facts and must never collapse into one another: the
 * first is a green light, the second is a stop. Keeping them apart is the whole
 * job of this file, which is why UNKNOWN is `count === null` and never
 * `count === 0`.
 *
 * Only MATERIAL publishes are gated. A minor publish strands nobody new, so it
 * is allowed to proceed against an unknown number — the guard exists to stop an
 * uninformed material publish, not to stop all work when the count is missing.
 *
 * Pure and dependency-free on purpose: no React, no I/O, nothing imported but a
 * type. That is what lets the only safety-critical logic on this screen be
 * tested without a DOM. See `radiusGuard.test.ts`.
 */

/**
 * The sentinel meaning "this number was computed against the CURRENT instant" —
 * what the Acceptance tab's "Check blast radius" button asks for, since it
 * sends no effective date at all.
 *
 * It exists because `null` was quietly doing two incompatible jobs in
 * `forEffectiveAt`: `loadRadius(changeType)` with no date recorded `null`
 * meaning "evaluated at now", while the publish modal's pending effective
 * instant is ALSO `null` until a date is picked, meaning "no date chosen yet".
 * `null === null`, so an at-now number satisfied the comparison for a dateless
 * modal — a number answering a different question than the one on screen, which
 * is the single failure this file exists to prevent. Two meanings sharing one
 * value cannot be told apart, so one of them gets a value of its own.
 *
 * A literal string is safe: the only other inhabitants of this field are ISO
 * 8601 instants (`2026-09-01T00:00:00.000Z`), and no instant is ever the six
 * characters `AT_NOW`. Be aware that TypeScript collapses `string | 'AT_NOW'`
 * back to `string` (and this app compiles with `strictNullChecks: false`), so
 * the compiler will not police this for you — the separation is enforced at
 * RUNTIME by the comparisons in `radiusAnswers`, and nowhere else.
 */
export const AT_NOW = 'AT_NOW';
export type AtNow = typeof AT_NOW;

/**
 * The instant a radius question is asked about, and the instant an answer is
 * scoped to: an ISO instant, or `AT_NOW`.
 *
 * `null` is deliberately NOT one of these. It is the ABSENCE of an instant —
 * "no date picked yet" on the question side, "nothing fetched yet" on the state
 * side — and absence answers nothing.
 */
export type EffectiveInstant = string | AtNow;

export interface RadiusState {
  loading: boolean;
  /** Acceptances stale after the publish. `null` means UNKNOWN — never 0. */
  count: number | null;
  total: number | null;
  error: string | null;
  /** The change type the number in `count` actually answers for. */
  forChangeType: ChangeType | null;
  /**
   * The effective instant the number in `count` answers for: an ISO instant, or
   * `AT_NOW` when it was computed against the current instant. `null` means NO
   * ANSWER YET — the idle state — and never "at now"; see `AT_NOW` above for
   * why those two must not share a value.
   */
  forEffectiveAt: EffectiveInstant | null;
}

/**
 * Nothing asked, nothing known. The state a freshly opened publish modal starts
 * in. `forEffectiveAt: null` here is "no answer yet" — the only thing `null`
 * means in this field.
 */
export const IDLE_RADIUS: RadiusState = {
  loading: false, count: null, total: null, error: null, forChangeType: null, forEffectiveAt: null,
};

/**
 * Does `state` answer the question `(changeType, effectiveAt)` that is on screen
 * right now?
 *
 * Every clause is load-bearing:
 *  - `loading`  — an in-flight answer answers the PREVIOUS question, if any.
 *  - `error`    — a failed fetch leaves no number at all.
 *  - `count`    — `null` is UNKNOWN. `0` passes this check, deliberately.
 *  - `changeType` non-null — with no change type chosen there is no question,
 *    and an unasked question cannot have been answered.
 *  - `effectiveAt` non-null — likewise for the date: a modal with no day picked
 *    has asked about no instant at all. Without this clause a state still
 *    holding `forEffectiveAt: null` would "answer" it by `null === null`.
 *  - the two `for*` comparisons — the number must have been computed FOR this
 *    change type and FOR this effective instant, not merely be lying around.
 *    `AT_NOW` is a value distinct from `null` and from every ISO instant, so an
 *    at-now number answers neither a dated question nor a dateless modal, and a
 *    dated number never answers an at-now question.
 */
export function radiusAnswers(
  state: RadiusState,
  changeType: ChangeType | null,
  effectiveAt: EffectiveInstant | null,
): boolean {
  return !state.loading
    && !state.error
    && state.count !== null
    && !!changeType
    && state.forChangeType === changeType
    && effectiveAt !== null
    && state.forEffectiveAt === effectiveAt;
}

/**
 * The number to hand to `PublishModal.staleAcceptanceCount`, or `undefined`
 * when it must render "—".
 *
 * `undefined` means UNKNOWN and nothing else. A genuine `0` is returned as `0`;
 * collapsing it to `undefined` would hide every honestly-zero publish behind a
 * dash, and collapsing `undefined` to `0` would ship a material change under
 * the sentence "this affects nobody".
 */
export function radiusCountFor(
  state: RadiusState,
  changeType: ChangeType | null,
  effectiveAt: EffectiveInstant | null,
): number | undefined {
  return radiusAnswers(state, changeType, effectiveAt) ? state.count ?? undefined : undefined;
}

export interface PublishDecision {
  ok: boolean;
  /** Present only when `ok` is false — the message shown inline in the modal. */
  reason?: string;
}

/**
 * May this publish proceed?
 *
 * Material: only against a number that answers this exact question. Anything
 * else — in flight, failed, or computed for a different change type or date —
 * is refused, because a silent wrong number here is precisely the failure this
 * screen exists to prevent.
 *
 * Minor (and an undecided change type, which the modal's own validation blocks
 * before it ever reaches here): allowed. A minor publish strands nobody new.
 */
export function canPublish(
  state: RadiusState,
  changeType: ChangeType | null,
  effectiveAt: EffectiveInstant | null,
): PublishDecision {
  if (changeType !== 'material') return { ok: true };
  if (radiusAnswers(state, changeType, effectiveAt)) return { ok: true };
  return {
    ok: false,
    reason: state.error
      ? `The blast radius for a material change could not be computed (${state.error}). `
        + 'Publishing is blocked until it can be: change the effective date, or switch the '
        + 'change type and back, to retry.'
      : 'Still computing how many acceptances this material change strands as of the effective '
        + 'date chosen. Publishing is blocked for the moment it takes — the number must be '
        + 'known before, not after.',
  };
}
